import deepmerge from 'deepmerge';
import { useState, useCallback } from 'react';
import { atom, selector, useRecoilRefresher_UNSTABLE, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import WRAP from 'WRAP-JS';
import { getMapContents } from '../components/map/Map';
import { deepCopy, removeProperty, setProperty } from '../utils/FPEUtils';
import { executeLinksAPI } from "../utils/LinksAPI";
import UnauthorizedError from "../UnauthorizedError"
import { userState } from '../store/UserState';
import { COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD, COMMUNICATE_ERROR_MESSAGE_WORKSPACE_MODE } from '../constants/ErrorMessage';

const receive = async (id) => {
	let mode = 'list';
	const config = {params: {}, errorHandle: {
		message: COMMUNICATE_ERROR_MESSAGE_WORKSPACE_MODE,
		additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
	}};
	if (id) {
		mode = 'detail';
		config.params.workspaceId = id;
		config.errorHandle.additionalMessage = COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
	}
	config.errorHandle.replacement = {mode: mode};
	const data = await executeLinksAPI(`fosterlinks/workspace/${mode}`, config)
		.catch(error => {
			if (error instanceof UnauthorizedError) {
				throw error;
			} else {
				return {};
			}
		});
	return deepCopy(data);
}

const send = async (data, options) => {
	const params = new URLSearchParams();
	let mode = 'register';
	for(const [key, value] of Object.entries(options)) {
		if (key === 'mode') {
			mode = value;
			continue;
		}
		const _values = [].concat(value);
		_values.forEach(v => {
			params.append(key, v);
		});
	}
	await executeLinksAPI(`fosterlinks/workspace/${mode}`,
		{
			method: mode === 'register' ? 'POST' : 'DELETE',
			headers: {
				'Content-Type': 'application/json'
			},
			params: params,
			data: data,
			errorHandle: {
				message: COMMUNICATE_ERROR_MESSAGE_WORKSPACE_MODE,
				replacement: {mode: mode},
				additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
			}
		})
		.then(() => {
			Promise.resolve();
		})
		.catch(error => {
			// Promise.resolve();
			if (error instanceof UnauthorizedError) {
				throw error;
			}
		});
}

const sendDefault = async (id) => {
	await executeLinksAPI('fosterlinks/workspace/default',
		{
			method: 'put',
			headers: {
				'Content-Type': 'application/json'
			},
			data: {
				'workspaceId': id,
			},
			errorHandle: {
				message: COMMUNICATE_ERROR_MESSAGE_WORKSPACE_MODE,
				replacement: {mode: 'default'},
				additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
			}
		})
		.then(() => {
			Promise.resolve();
		})
		.catch((error) => {
			// Promise.resolve();
			if (error instanceof UnauthorizedError) {
				throw error;
			}
		});
}

const DISCONTINUE = [
	'WX_ID_RADAR.Japan',
	'WX_ID_RADAR.echotop',
];

const workSpaceListState = selector({
	key: 'workSpaceListState',
	get: () => receive(),
});

export const useWorkSpaceState = () => {
	const workSpaceList = useRecoilValue(workSpaceListState);
	const refresher = useRecoilRefresher_UNSTABLE(workSpaceListState);

	const setDefault = useCallback(async (workspaceId) => {
		await sendDefault(workspaceId);
	}, []);

	return {
		workSpaceList,
		refreshWorkSpaceList: refresher,
		setDefault
	}
}

export const savedDisplayOperationState = atom({
	key: 'savedDisplayOperationState',
	default: selector({
		key: 'savedDisplayOperationStateSelector',
		get: async ({get}) => {
			const workspaceId = get(userState).workspaceId;
			if (workspaceId) {
				let data = await receive(workspaceId);
				if (data instanceof Object) {
					// discontinue filter
					for (const discontinueKey of DISCONTINUE) {
						removeProperty(data, discontinueKey);
					}
					return data;
				} else {
					return {};
				}
			}
			return {};
		}
	})
});

const displayOperationState = atom({
	key: 'displayOperationState',
	default: savedDisplayOperationState
});

const useWrapState = (key, value, stateFunc) => {
	const setDisplayOperationState = useSetRecoilState(displayOperationState);
	const [_value, _setValue] = stateFunc(value);
	const set = useCallback((v) => {
		_setValue(v);
		setDisplayOperationState((old) => {
			let saveKey = key;
			let assign = false;
			if (saveKey instanceof Object) {
				const {path, key: _key, merge, ...other} = key;
				saveKey = path;
				if (_key) {
					v = {[_key]: v};
				}
				if (merge) {
					assign = true;
				}
				v = Object.assign({}, v, other);
			}
			// console.log(`${saveKey}: ${JSON.stringify(v)}`);
			return setProperty(deepCopy(old), saveKey, v, {assign});
		});
	}, [key, setDisplayOperationState, _setValue]);
	return [_value, set];
}

export const useDisplayOperationState = (key, value) => {
	return useWrapState(key, value, useState);
};

export const useDisplayOperationRecoilState = (key, value) => {
	return useWrapState(key, value, useRecoilState);
}

export const useDisplayOperation = () => {
	const refresh = useRecoilRefresher_UNSTABLE(workSpaceListState);
	const state = useRecoilValue(displayOperationState);
	const save = useCallback(async (options) => {
		const center = WRAP.Geo.getCenterPoint();
		const zoom = WRAP.Geo.getZoom();
		const cloneState = deepmerge({}, state);
		const mapContents = getMapContents();
		Object.keys(mapContents).forEach(key=>{
			if (typeof mapContents[key].getExtendedOperation === 'function') {
				const ext = mapContents[key].getExtendedOperation();
				if (ext) {
					if (!(key in cloneState)) {
						cloneState[key] = {};
					}
					Object.assign(cloneState[key], ext);
				}
			}
		})
		// console.log(JSON.stringify(cloneState));
		return await send(
			Object.assign(cloneState, {
				"MAP": {
					"centerLon": center.lonDegree(),
					"centerLat": center.latDegree(),
					"zoom": zoom,
				}
			}),
			options
		);
	}, [state]);

	const _regist = useCallback((name) => {
		return save({
			name,
			mode: 'register'
		})
		.then(() => {
			refresh();
		});
	}, [save, refresh]);

	const _delete = useCallback((preferenceIds) => {
		return save({
			workspaceId: preferenceIds,
			mode: 'delete'
		})
		.then(() => {
			refresh();
		});
	}, [save, refresh]);

	return {
		regist: _regist,
		delete: _delete,
	}
}

export const useChangeDisplayOperation = () => {
  const setDisplayOperationState = useSetRecoilState(displayOperationState);
  const set = useCallback((v, isMarge = true, isForce = false) => {
    setDisplayOperationState((old) => {
      if (isMarge) {
        return deepmerge(old, v);
      } else if (isForce === false) {
        // SCREEN_SETTINGは引き継ぐ
        const screenSetting = old.SCREEN_SETTING || null;
        return {
          ...v,
          ...(screenSetting !== null && { SCREEN_SETTING: screenSetting }),
        };
      } else {
        return v;
      }
    });
  }, [setDisplayOperationState]);
  return set;
};

// const workSpaceIdValueAtom = atom({
// 	key: 'workSpaceIdValueAtom',
// 	default: null,
// });

// export const workSpaceIdValue = selector({
// 	key: 'workSpaceIdValue',
// 	get: ({get}) => get(workSpaceIdValueAtom) || sessionStorage.getItem('workspaceId'),
// 	set: ({set}, newValue) => {
// 		set(workSpaceIdValueAtom, newValue);
// 		if (newValue !== undefined && newValue !== null && newValue !== "") {
// 			sessionStorage.setItem('workspaceId', newValue);
// 		} else {
// 			sessionStorage.removeItem('workspaceId');
// 		}
// 	},
// });

export default function WorksProfile({children}) {
	useRecoilValue(savedDisplayOperationState);

	return children || null;
}
